import { css } from "@emotion/css";
import type { DropdownButtonState } from "@octopusdeploy/design-system-components";
import { ChevronUpIcon, resetStyles, ChevronDownIcon, OctopusIcon } from "@octopusdeploy/design-system-components";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { SpaceResource } from "@octopusdeploy/octopus-server-client";
import type { ValuesAsKeys } from "@octopusdeploy/type-utils";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorAlert } from "../Alert/ErrorAlert";
import { DropdownButton } from "../DropdownButton";
import { SpaceCallToActionPanel } from "./SpaceCallToActionPanel";
import { SpaceLogo } from "./SpaceLogo";
import { SpaceSearchPanel } from "./SpaceSearchPanel/SpaceSearchPanel";
import { useTrackSpaceSwitcherOpenEffect, useTrackSpaceSwitcherStartedSearchEffect, useInstrumentAddNewSpaceWithAnalytics, useTrackSpaceSelectedCallback } from "./analytics/useDispatchSpaceSwitcherInteraction";
export const SpaceCreationRestriction = {
    NoPermission: "No Permission",
    LicenseLimitReached: "License Limit Reached",
} as const;
export type SpaceCreationRestrictionValue = ValuesAsKeys<typeof SpaceCreationRestriction>;
export interface ControlledSpaceSwitcherProps {
    currentSpace: SpaceResource | undefined;
    spaces: SpaceResource[];
    isLoading: boolean;
    onNewSpaceRequested: () => void;
    onUnlockMoreSpacesRequested: () => void;
    getSpaceHref: (space: SpaceResource) => string;
    spaceCreationRestriction?: SpaceCreationRestrictionValue;
    onFetchSpacesRequested: () => void;
    dropdownButtonState: DropdownButtonState;
    error: Error | undefined;
    onCloseError: () => void;
}
export function ControlledSpaceSwitcher({ currentSpace, onNewSpaceRequested, onUnlockMoreSpacesRequested, getSpaceHref, onFetchSpacesRequested, spaces, isLoading, spaceCreationRestriction, dropdownButtonState, error, onCloseError, }: ControlledSpaceSwitcherProps) {
    const { isOpen, closeDropdown } = dropdownButtonState;
    const [searchValue, setSearchValue] = useState("");
    useEffect(() => {
        if (isOpen) {
            onFetchSpacesRequested();
        }
    }, [isOpen, onFetchSpacesRequested]);
    useEffect(() => {
        if (!isOpen) {
            setSearchValue("");
        }
    }, [isOpen]);
    const onNewSpaceRequestedWithAnalytics = useInstrumentAddNewSpaceWithAnalytics(onNewSpaceRequested);
    useTrackSpaceSwitcherOpenEffect(isOpen);
    useTrackSpaceSwitcherStartedSearchEffect(searchValue, isOpen);
    const trackSpaceSelected = useTrackSpaceSelectedCallback();
    const handleSpaceClicked = useCallback(() => {
        trackSpaceSelected();
        closeDropdown();
    }, [closeDropdown, trackSpaceSelected]);
    const loadedCurrentSpace = currentSpace ? spaces?.find((space) => space.Id === currentSpace.Id) : undefined;
    const latestCurrentSpace = loadedCurrentSpace ?? currentSpace;
    const hasMultipleSpaces = spaces !== undefined && spaces.length > 1;
    const errorAlert = error ? <ErrorAlert error={error} onClose={onCloseError} accessibleName="Space switcher error"/> : undefined;
    let dropdownContent = undefined;
    const isWithinLicenseLimit = spaceCreationRestriction !== SpaceCreationRestriction.LicenseLimitReached;
    if (hasMultipleSpaces) {
        dropdownContent = (<SpaceSearchPanel searchValue={searchValue} onSearchValueChanged={setSearchValue} spaces={spaces} canCreateNewSpace={spaceCreationRestriction === undefined} isWithinLicenseLimit={isWithinLicenseLimit} onNewSpaceRequested={onNewSpaceRequestedWithAnalytics} onUnlockMoreSpacesRequested={onUnlockMoreSpacesRequested} getSpaceHref={getSpaceHref} isLoading={isLoading} errorAlert={errorAlert} onSpaceSelected={handleSpaceClicked}/>);
    }
    else if (spaceCreationRestriction !== SpaceCreationRestriction.NoPermission) {
        const callToActionLabel = isWithinLicenseLimit ? "Create new space" : "Unlock more spaces";
        const onCallToActionRequested = isWithinLicenseLimit ? onNewSpaceRequested : onUnlockMoreSpacesRequested;
        dropdownContent = <SpaceCallToActionPanel callToActionButtonLabel={callToActionLabel} onCallToActionRequested={onCallToActionRequested} isLoading={isLoading} errorAlert={errorAlert}/>;
    }
    const dropdownButtonCanOpen = !!dropdownContent;
    return (<>
            <DropdownButton.Button className={spaceButtonRootStyles} dropdownButtonState={dropdownButtonState} disabled={!dropdownButtonCanOpen} accessibleName="Space switcher">
                <SpaceImage currentSpace={latestCurrentSpace}/>
                <SpaceContent currentSpace={latestCurrentSpace}/>
                {dropdownButtonCanOpen && (isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />)}
            </DropdownButton.Button>
            {isOpen && dropdownButtonCanOpen && (<DropdownButton.Dropdown dropdownButtonState={dropdownButtonState} accessibleName="Space switcher dropdown">
                    {dropdownContent}
                </DropdownButton.Dropdown>)}
        </>);
}
function SpaceImage({ currentSpace }: {
    currentSpace: SpaceResource | undefined;
}) {
    return <div className={spaceImageStyles}>{currentSpace ? <SpaceLogo space={currentSpace}/> : <OctopusIcon />}</div>;
}
function SpaceContent({ currentSpace }: {
    currentSpace: SpaceResource | undefined;
}) {
    return <>{currentSpace && <span className={spaceContentStyles}>{currentSpace.Name}</span>}</>;
}
const spaceButtonRootStyles = css({
    ...resetStyles.button,
    font: text.navigation.button.medium,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    gap: space[16],
    transitionTimingFunction: "ease-out",
    transitionProperty: "background-color, border-color, outline, color, fill",
    transitionDuration: "0.2s",
    borderRadius: borderRadius.medium,
    paddingRight: space[16],
    maxWidth: "15rem",
    height: "2.5rem",
    color: themeTokens.color.navigation.button.text.primary,
    background: themeTokens.color.navigation.button.background.primary.default,
    ":hover": {
        background: themeTokens.color.navigation.button.background.primary.hover,
    },
    ":focus-visible": {
        boxShadow: themeTokens.shadow.focus.default,
    },
    ":active, &[aria-expanded='true']": {
        background: themeTokens.color.navigation.button.background.primary.pressed,
    },
});
const spaceImageStyles = css({
    boxSizing: "border-box",
    alignSelf: "stretch",
    aspectRatio: "1",
    borderRadius: borderRadius.medium,
    outline: `${borderWidth["1"]} solid rgba(255, 255, 255, 0.4)`,
    outlineOffset: `-${borderWidth["1"]}`,
    minWidth: "2.5rem",
    maxWidth: "2.5rem",
    overflow: "clip",
    justifyContent: "center",
    display: "flex",
    alignItems: "center",
    backgroundColor: colorScales.white,
});
const spaceContentStyles = css({
    flex: 1,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
});
